<template>
  <div class="modal-tip">

    <!-- ========================= -->
    <!-- STEP 1 : USER LOOKUP -->
    <!-- ========================= -->
    <div v-if="step === 'lookup'" class="tip-lookup">

      <div class="tip-title">
        <span class="gradient-green">{{ $t('modal.217') }}</span>
      </div>

      <div class="lookup-input">
        <input
          v-model="lookupValue"
          type="text"
          placeholder="Username or User ID"
        />
      </div>

      <button
        class="button-tip"
        @click="lookupUser"
        :disabled="lookupValue.length < 3"
      >
        <div class="button-inner">
          {{ $t('modal.findUser') }}
        </div>
      </button>

    </div>

    <!-- ========================= -->
    <!-- STEP 2 : TIP SCREEN -->
    <!-- ========================= -->
    <div v-else-if="step === 'tip'">

      <div class="tip-title">
        <span class="gradient-green">{{ $t('modal.217') }}</span>
      </div>

      <div class="tip-avatar">
        <transition name="fade" mode="out-in">
          <div
            v-if="generalUserInfo.data === null || generalUserInfo.loading === true"
            class="avatar-loading"
          ></div>

          <div
            v-else
            class="avatar-content"
            :class="[
              'content-' + modalGetLevelColor,
              'content-' + generalUserInfo.data.rank
            ]"
          >
            <AvatarImage :image="generalUserInfo.data.avatar" />
          </div>
        </transition>
      </div>

      <div class="tip-username">
        <transition name="fade" mode="out-in">
          <div
            v-if="generalUserInfo.data === null || generalUserInfo.loading === true"
            class="username-loading"
          ></div>

          <div v-else class="username-content">
            <span v-html="generalUserInfo.data.username"></span>

            <BoxLevel
              v-if="['admin','mod','partner'].includes(generalUserInfo.data.rank) === false"
              :level="generalUserInfo.data.level"
              :color="modalGetLevelColor"
            />

            <BoxRank
              v-if="modalGetRank !== null"
              :rank="modalGetRank"
            />
          </div>
        </transition>
      </div>

      <div class="tip-date">
        <transition name="fade" mode="out-in">
          <div
            v-if="generalUserInfo.data === null || generalUserInfo.loading === true"
            class="date-loading"
          ></div>

          <div v-else class="date-content">
            {{ $t('modal.218') }} {{ modalGetDate }}
          </div>
        </transition>
      </div>

      <div class="tip-input">
        <input
          v-model="modalAmount"
          @input="modalValidateInput"
          type="text"
          placeholder="TIP AMOUNT"
        />

        <img src="@/assets/img/rivocoin.png" alt="icon" />

        <button
          class="button-tip"
          @click="modalTipButton"
          :disabled="socketSendLoading !== null"
        >
          <div class="button-inner">
            <transition name="fade" mode="out-in">
              <ButtonLoading
                v-if="socketSendLoading === 'UserTip'"
                key="loading"
              />
              <div v-else class="inner-content" key="content">
                {{ $t('modal.219') }}
              </div>
            </transition>
          </div>
        </button>
      </div>

      <div class="tip-info">
        {{ $t('modal.220') }}
      </div>

      <!-- optional: user değiştirme -->
      <button class="change-user-btn" @click="step = 'lookup'">
        {{ $t('modal.changeUser') }}
      </button>

    </div>

  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import AvatarImage from '@/components/AvatarImage';
import BoxLevel from '@/components/BoxLevel';
import BoxRank from '@/components/BoxRank';
import ButtonLoading from '@/components/ButtonLoading';

export default {
  name: 'ModalTip',

  components: {
    AvatarImage,
    BoxLevel,
    BoxRank,
    ButtonLoading
  },

  data() {
    return {
      modalAmount: null,
      lookupValue: '',
      step: 'lookup' // lookup | tip
    };
  },

  created() {
    // Eğer modal mevcut user context ile açıldıysa direkt tip ekranı
    if (this.generalUserInfo && this.generalUserInfo.data && this.generalUserInfo.data._id) {
      this.step = 'tip';
    }
  },

  computed: {
    ...mapGetters([
      'socketSendLoading',
      'modalsData',
      'generalUserInfo'
    ]),

    modalGetLevelColor() {
      if (!this.generalUserInfo || !this.generalUserInfo.data) return '';

      const level = this.generalUserInfo.data.level;

      if (level >= 2 && level < 26) return 'blue';
      if (level >= 26 && level < 51) return 'green';
      if (level >= 51 && level < 76) return 'orange';
      if (level >= 76 && level < 100) return 'red';
      if (level >= 100) return 'purple';

      return '';
    },

    modalGetRank() {
      if (!this.generalUserInfo || !this.generalUserInfo.data) return null;
      return this.generalUserInfo.data.rank !== 'user'
        ? this.generalUserInfo.data.rank
        : null;
    },

    modalGetDate() {
      if (!this.generalUserInfo || !this.generalUserInfo.data?.createdAt) return '';
      const date = new Date(this.generalUserInfo.data.createdAt);
      return (date.getMonth() + 1) + '/' + date.getDate() + '/' + date.getFullYear();
    }
  },

  watch: {
    // Lookup sonrası user geldiğinde otomatik tip ekranına geç
    'generalUserInfo.data'(val) {
      if (val && val._id) {
        this.step = 'tip';
      }
    }
  },

  methods: {
    ...mapActions([
      'notificationShow',
      'userSendUserTipSocket',
      'generalGetUserInfoSocket'
    ]),

    modalValidateInput() {
      if (this.modalAmount === null) return;
      this.modalAmount = this.modalAmount.toString().replace(/[^\d.]/g, '');
    },

    lookupUser() {
      const value = this.lookupValue.trim();
      if (!value) return;

      this.generalGetUserInfoSocket({
        query: value,
        source: 'Tip'
      });
    },

    modalTipButton() {
      const amount = Number(this.modalAmount);

      if (!Number.isFinite(amount) || amount <= 0) {
        this.notificationShow({
          type: 'error',
          message: 'Your entered tip amount is invalid.'
        });
        return;
      }

      if (!this.generalUserInfo || !this.generalUserInfo.data?._id) {
        this.notificationShow({
          type: 'error',
          message: 'User not selected.'
        });
        return;
      }

      const data = {
        receiverId: this.generalUserInfo.data._id,
        amount
      };

      this.userSendUserTipSocket(data);
    }
  }
};
</script>


<style scoped>
.modal-tip {
    width: 773px;
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 35px 20px 48px 20px;
    border-radius: 15px;
    background: var(--background);
}

/* Title */
.modal-tip .tip-title {
    text-align: center;
    font-size: 32px;
    font-weight: 900;
}

/* Avatar */
.modal-tip .tip-avatar {
    margin-top: 35px;
}
.modal-tip .avatar-loading,
.modal-tip .username-loading,
.modal-tip .date-loading {
    position: relative;
    overflow: hidden;
    background-color: #072435;
}
.modal-tip .avatar-loading {
    width: 95px;
    height: 95px;
}
.modal-tip .username-loading {
    width: 150px;
    height: 30px;
    border-radius: 5px;
}
.modal-tip .date-loading {
    width: 110px;
    height: 19px;
    border-radius: 5px;
}
.modal-tip .avatar-loading::after,
.modal-tip .username-loading::after,
.modal-tip .date-loading::after {
    width: 100%;
    height: 100%;
    position: absolute;
    content: '';
    top: 0;
    left: 0;
    animation: loading_animation 1s ease infinite;
    background: linear-gradient(to right, #ffffff00 0%, rgba(255, 255, 255, 0.1) 50%, #ffffff00 100%);
}

/* Avatar content */
.modal-tip .avatar-content {
    width: 95px;
    height: 95px;
    display: flex;
    justify-content: center;
    align-items: flex-end;
    border-radius: 15px;
    overflow: hidden;
}
.modal-tip .avatar-content .avatar-image {
    width: 78px;
    height: 78px;
}

/* Username & date */
.modal-tip .tip-username {
    margin-top: 18px;
}
.modal-tip .username-content {
    display: flex;
    align-items: center;
    font-size: 22px;
    font-weight: 700;
    color: #ffffff;
}
.modal-tip .tip-date {
    margin-top: 5px;
}
.modal-tip .date-content {
    font-size: 14px;
    font-weight: 400;
    color: #49687d;
}

/* Input */
.modal-tip .tip-input {
    width: 448px;
    height: 72px;
    position: relative;
    margin-top: 35px;
    padding: 1px;
}
.modal-tip .tip-input input {
    width: 100%;
    height: 100%;
    padding: 0 127px 0 45px;
    font-size: 16px;
    font-weight: 600;
    color: #ffffff;
    background: var(--background2);
    border-radius: 10px;
}
.modal-tip .tip-input input::placeholder {
    color: #5e768e;
}
.modal-tip .tip-input img {
    width: 20px;
    height: 20px;
    position: absolute;
    top: 50%;
    left: 15px;
    transform: translate(0, -50%);
}
.modal-tip .tip-input button.button-tip {
    width: 92px;
    height: 39px;
    position: absolute;
    top: 50%;
    right: 25px;
    transform: translate(0, -50%);
}
.modal-tip .tip-input button.button-tip .button-inner {
    width: 100%;
    height: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    background: var(--buttonyellow);
    box-shadow: var(--buttonyellowbox);
    border-radius: 10px;
    text-shadow: -1px -1px 0 var(--roo-btn-text-stroke-color, #C56A18),
                 1px -1px 0 var(--roo-btn-text-stroke-color, #C56A18),
                -1px 1px 0 var(--roo-btn-text-stroke-color, #C56A18),
                 1px 1px 0 var(--roo-btn-text-stroke-color, #C56A18);
    color: #fff;
}
.modal-tip .tip-input button.button-tip .inner-content {
    font-size: 16px;
    font-weight: 800;
    color: #ffffff;
}

/* Info */
.modal-tip .tip-info {
    width: 100%;
    margin-top: 35px;
    padding: 0 42px;
    text-align: center;
    font-size: 14px;
    font-weight: 400;
    color: #49687d;
}

/* Loading animation */
@keyframes loading_animation {
    0% { transform: translateX(-100%); }
    50% { transform: translateX(100%); }
    100% { transform: translateX(100%); }
}

/* Tablet */
@media only screen and (max-width: 793px) {
    .modal-tip {
        width: calc(100vw - 20px);
    }
}

/* Mobil (bottom sheet) */
@media only screen and (max-width: 500px) {
    .modal-tip {
        width: 100vw;                  /* tam genişlik */
        max-width: 100%;
        border-radius: 20px 20px 0 0;  /* sadece üst köşeler */
        padding: 55px 15px 48px 15px;
    }

    .modal-tip .tip-input {
        width: 100%;
    }

    .modal-tip .tip-info {
        padding: 0;
    }
}

 /* Lookup input */
.tip-lookup .lookup-input input {
  width: 100%;
  height: 64px;
  padding: 0 20px;
  font-size: 16px;
  font-weight: 600;
  color: #ffffff;
  background: var(--background2);
  border-radius: 12px;
 
  outline: none;
}

.tip-lookup .lookup-input input::placeholder {
  color: #5e768e;
}

.tip-lookup .lookup-input input:focus {
  border-color: var(--buttonyellow);
}

.tip-lookup .button-tip {
  width: 180px;
  height: 46px;
}

.tip-lookup .button-tip .button-inner {
  width: 100%;
  height: 100%;
  font-size: 15px;
  font-weight: 800;
      margin-top: 25px;
    background: var(--active-button);
    border: none;
    font-size: 13px;
    font-weight: 600;
    color: #fff;
    cursor: pointer;
    transition: color 0.2s ease;
    padding: 10px;
    border-radius: 10px;
    left: 50%;
}

.tip-lookup .button-tip:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* Change user button */
.change-user-btn {
    margin-top: 25px;
    background: var(--active-button);
    border: none;
    font-size: 13px;
    font-weight: 600;
    color: #fff;
    cursor: pointer;
    transition: color 0.2s ease;
    padding: 10px;
    border-radius: 10px;
}

 

@media only screen and (max-width: 500px) {
  .tip-lookup .lookup-input {
    width: 100%;
  }

  .tip-lookup .button-tip {
    width: 100%;
  }
}

</style>

